// CP862 (IBM Hebrew DOS) byte → Unicode — same encoding as Hachshara files
const CP862 = {};
const HEB_LETTERS = "אבגדהוזחטיךכלםמןנסעףפץצקרשת";
for (let i = 0; i < HEB_LETTERS.length; i++) {
  CP862[0x80 + i] = HEB_LETTERS[i];
}

function decodeVisual(bytes) {
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i];
    if (b >= 0x30 && b <= 0x39) out += String.fromCharCode(b);
    else if (b === 0x20) out += " ";
    else if (b >= 0x80 && CP862[b]) out += CP862[b];
    else out += " ";
  }
  return out;
}

function decodeHebField(bytes) {
  // Hebrew stored in visual (right-to-left) order; reverse for logical Unicode order.
  const raw = Array.from(bytes)
    .map(b => (b >= 0x80 && CP862[b]) ? CP862[b] : (b === 0x20 ? ' ' : (b >= 0x20 && b < 0x80 ? String.fromCharCode(b) : '')))
    .join('').trimStart();
  const reversed = [...raw].reverse().join('').trim();
  // Latin letter sequences get character-flipped by the full-string reverse; fix them.
  // Only target letter runs — spaces, digits and punctuation must stay in place.
  return reversed.replace(/[A-Za-z]+/g, w => [...w].reverse().join(''));
}

function reverseHeb(s) {
  return [...s.trim()].reverse().join("").trim();
}

function readBalance(bytes) {
  const s = String.fromCharCode(...bytes).trim();
  const n = parseInt(s, 10);
  return isNaN(n) ? 0 : n;
}

function formatDate(d8) {
  if (!/^\d{8}$/.test(d8)) return null;
  const dd = d8.slice(0, 2), mm = d8.slice(2, 4), yyyy = d8.slice(4, 8);
  if (+dd < 1 || +dd > 31 || +mm < 1 || +mm > 12 || +yyyy < 1900 || +yyyy > 2100) return null;
  return `${dd}/${mm}/${yyyy}`;
}

// Scan the first line terminator to determine stride (line_len + newline bytes).
function detectStride(bytes) {
  for (let i = 50; i < Math.min(bytes.length, 10000); i++) {
    if (bytes[i] === 0x0D && bytes[i + 1] === 0x0A) return i + 2; // CRLF
    if (bytes[i] === 0x0A) return i + 1;                            // LF only
  }
  return 2001; // fallback: 2000-byte records + LF
}

// ────────────────────────────────────────────────────────────────────────────
// Parse RP or SP file (personal records) → Map memberId → personal info.
//
// Field layout (byte positions, 0-indexed, same as Hachshara SP):
//   0–15  : policy number (16 digits)
//   6–15  : member ID (10 digits) — used to link to RM
//  25–33  : ת"ז (9 digits)
//  34–42  : zeros
//  43–57  : last name  (15 bytes, visual Hebrew, right-aligned)
//  58–72  : first name (15 bytes, visual Hebrew, right-aligned)
// 113–127 : city       (15 bytes, visual Hebrew)
// 128–159 : region with zip(5) + gender(ז|נ) + 2 digits + DOB(DDMMYYYY)
// ────────────────────────────────────────────────────────────────────────────
// tazNames (optional Map) collects taz → "שם פרטי שם משפחה" for EVERY record,
// including family members insured on the same policy (different taz, same member).
function parsePersonalFile(content, tazNames = null) {
  const bytes = new Uint8Array(content);
  if (bytes.length < 60) return new Map();

  const stride = detectStride(bytes);
  const lineLen = stride - (bytes[stride - 1] === 0x0A && bytes[stride - 2] === 0x0D ? 2 : 1);
  const map = new Map();

  for (let offset = 0; offset + lineLen <= bytes.length; offset += stride) {
    const line = bytes.subarray(offset, offset + lineLen);

    const memberId = String.fromCharCode(...line.subarray(6, 16));
    if (!/^\d{10}$/.test(memberId)) continue;

    const tazRaw = String.fromCharCode(...line.subarray(25, 34));
    const taz = /^\d{9}$/.test(tazRaw) && tazRaw !== "000000000" ? tazRaw : null;

    const lastName  = reverseHeb(decodeVisual(line.subarray(43, 58)));
    const firstName = reverseHeb(decodeVisual(line.subarray(58, 73)));
    const city      = reverseHeb(decodeVisual(line.subarray(113, 128))) || null;

    const region = decodeVisual(line.subarray(128, 160));
    let gender = null, birthDate = null;
    const m = region.match(/\d{5}([זנ])\d{2}(\d{8})/);
    if (m) {
      gender    = m[1] === "ז" ? "זכר" : "נקבה";
      birthDate = formatDate(m[2]);
    }

    if (tazNames && taz && !tazNames.has(taz)) {
      const fullName = [firstName, lastName].filter(Boolean).join(" ");
      if (fullName) tazNames.set(taz, fullName);
    }

    if (!map.has(memberId)) {
      map.set(memberId, {
        taz,
        firstName: firstName || null,
        lastName:  lastName  || null,
        city:      city      || null,
        gender,
        birthDate,
      });
    }
  }
  return map;
}

// ────────────────────────────────────────────────────────────────────────────
// Parse SB (balance summary) → Map memberId → { startDate }.
//
// Field layout (same as Hachshara SB):
//   6–15 : member ID (10 digits)
//  22–29 : start date DDMMYYYY
// ────────────────────────────────────────────────────────────────────────────
function parseSBFile(content) {
  const bytes = new Uint8Array(content);
  if (bytes.length < 30) return new Map();

  const stride = detectStride(bytes);
  const lineLen = stride - (bytes[stride - 1] === 0x0A && bytes[stride - 2] === 0x0D ? 2 : 1);
  const map = new Map();

  for (let offset = 0; offset + lineLen <= bytes.length; offset += stride) {
    const line = bytes.subarray(offset, offset + lineLen);
    const memberId = String.fromCharCode(...line.subarray(6, 16));
    if (!/^\d{10}$/.test(memberId)) continue;
    const d8 = String.fromCharCode(...line.subarray(22, 30));
    const startDate = formatDate(d8);
    if (startDate) map.set(memberId, { startDate });
  }
  return map;
}

// ────────────────────────────────────────────────────────────────────────────
// Parse RM file (financial savings policies — פוליסות חיסכון פיננסי).
//
// Field layout (Harel-specific stride 985+CRLF):
//   0–9   : member ID (10 digits) — links to RP/SP via policy[6:16]
//  10–18  : product code (9 digits)
//  19–43  : plan name (visual Hebrew, right-aligned, reverse for logical RTL)
//  85+    : fund entries: name(15 bytes) + balance(15 digits), every 30 bytes
//           balances stored in agorot (÷100 to get shekels)
// ────────────────────────────────────────────────────────────────────────────
const FUND_FIELD_START  = 85;
const FUND_FIELD_STRIDE = 30; // name(15) + balance(15)
const MAX_FUNDS         = 10;

function parseRMFile(content, personalMap = new Map(), sbMap = new Map()) {
  const bytes = new Uint8Array(content);
  if (bytes.length < 100) return [];

  const stride = detectStride(bytes);
  const lineLen = stride - (bytes[stride - 1] === 0x0A && bytes[stride - 2] === 0x0D ? 2 : 1);
  const members = [];

  for (let offset = 0; offset + lineLen <= bytes.length; offset += stride) {
    const line = bytes.subarray(offset, offset + lineLen);
    const rawId = String.fromCharCode(...line.subarray(0, 10));
    if (!/^\d{10}$/.test(rawId) || rawId === "0000000000") continue;

    const personal = personalMap.get(rawId) || {};
    const taz = personal.taz || rawId.slice(0, 9);

    // Plan name: bytes 19–43, visual Hebrew stored right-to-left
    const tochnit = decodeHebField(line.subarray(19, 44)) || null;

    const funds = [];
    for (let i = 0; i < MAX_FUNDS; i++) {
      const nameStart = FUND_FIELD_START + i * FUND_FIELD_STRIDE;
      const balStart  = nameStart + 15;
      if (balStart + 15 > lineLen) break;

      const rawBalance = readBalance(line.subarray(balStart, balStart + 15));
      if (rawBalance === 0) continue;

      // Balances are stored in agorot (1/100 shekel)
      const balance = Math.round(rawBalance / 100);
      const name = decodeHebField(line.subarray(nameStart, nameStart + 15));
      funds.push({ name: name || `קרן ${i + 1}`, balance });
    }

    const sb = sbMap.get(rawId);

    // Management fees: accumulation (64-67) and deposit (68-71)
    const rawTzv = String.fromCharCode(...line.subarray(64, 68));
    const dmeiTzvPct = /^\d{4}$/.test(rawTzv) ? parseInt(rawTzv, 10) / 100 : null;
    const rawHaf = String.fromCharCode(...line.subarray(68, 72));
    const dmeiHafPct = /^\d{4}$/.test(rawHaf) ? parseInt(rawHaf, 10) / 100 : null;

    members.push({
      rawId,
      taz,
      firstName:   personal.firstName || null,
      lastName:    personal.lastName  || null,
      city:        personal.city      || null,
      gender:      personal.gender    || null,
      birthDate:   personal.birthDate || null,
      startDate:   sb?.startDate      || null,
      tochnit,
      productType: 'savings',
      dmeiTzvPct,
      dmeiHafPct,
      funds,
    });
  }
  return members;
}

// ────────────────────────────────────────────────────────────────────────────
// Parse RB file (health insurance products — מוצרי בריאות).
//
// Each line = one coverage/rider. Field layout (line length 150 bytes + CRLF):
//   0–15  : policy number (16 digits)
//   6–15  : member ID (10 digits)
//  16–19  : product code (4 chars)
//  22–29  : start date DDMMYYYY
//  32–33  : payment duration (months)
//  46–51  : monthly premium × 1000 (NIS·milli, confirmed by binary analysis)
//  70–78  : ת"ז of insured (9 digits) — each rider may insure a different person
//  50–149 : coverage name (visual Hebrew + reversed ASCII, use decodeHebField)
//
// Returns Map: memberId → { taz, coverages: [{name, productCode, monthlyPremium,
//                                              startDate, duration, insuredTaz}] }
// ────────────────────────────────────────────────────────────────────────────
function parseRBFile(content) {
  const bytes = new Uint8Array(content);
  if (bytes.length < 82) return new Map();

  const stride = detectStride(bytes);
  const lineLen = stride - (bytes[stride - 1] === 0x0A && bytes[stride - 2] === 0x0D ? 2 : 1);
  const map = new Map();

  for (let offset = 0; offset + 80 <= bytes.length; offset += stride) {
    const line = bytes.subarray(offset, offset + Math.min(lineLen, bytes.length - offset));

    const policyStr = String.fromCharCode(...line.subarray(0, 16));
    if (!/^\d{16}$/.test(policyStr)) continue;

    // Harel splits a family policy into sub-policies that differ only in the
    // LAST digit (…0 = policy owner, …3 = spouse + children). Group by the
    // 15-digit base so the whole family lands in one record, keyed under the
    // owner (the lowest-suffix sub-policy, processed first in file order).
    const basePolicy = policyStr.slice(0, 15);
    const suffix     = parseInt(policyStr.slice(15), 10);

    const productCode = String.fromCharCode(...line.subarray(16, 20)).trim();

    // TAZ of THIS insured person (may differ across riders in a family policy)
    let insuredTaz = null;
    if (line.length > 78) {
      const tazRaw = String.fromCharCode(...line.subarray(70, 79));
      if (/^\d{9}$/.test(tazRaw) && tazRaw !== '000000000') insuredTaz = tazRaw;
    }

    // Start date: bytes 22–29 DDMMYYYY
    const startRaw = String.fromCharCode(...line.subarray(22, 30));
    const startDate = /^\d{8}$/.test(startRaw) ? formatDate(startRaw) : null;

    // Payment duration: bytes 32–33 (2 digits)
    const durRaw = String.fromCharCode(...line.subarray(32, 34));
    const duration = /^\d{2}$/.test(durRaw) ? parseInt(durRaw, 10) : null;

    // Sum insured: bytes 34–43 (first 9 digits of the 12-digit field, agorot ÷100)
    const sumRaw = String.fromCharCode(...line.subarray(34, 43));
    const sumInsured = /^\d{9}$/.test(sumRaw) ? parseInt(sumRaw, 10) / 100 : 0;

    // Monthly premium: bytes 46–51 (6 digits) ÷ 1000 = NIS with 3 decimal places
    const premRaw = String.fromCharCode(...line.subarray(46, 52));
    const monthlyPremium = /^\d{6}$/.test(premRaw) ? Math.round(parseInt(premRaw, 10)) / 1000 : 0;

    // Coverage name: decode visual Hebrew+ASCII from bytes 50–149, clean trailing codes
    let fundName = '';
    if (line.length > 52) {
      const raw = decodeHebField(line.subarray(50, Math.min(line.length, 150)));
      fundName = raw
        .split(/\s{4,}/)[0]              // cut at 4+ spaces (separates name from tail data)
        .replace(/\s+\d{1,3}\s*$/, '')   // remove trailing 2–3 digit category code
        .trim();
    }
    if (!fundName) fundName = productCode ? `ביטוח ${productCode}` : 'ביטוח';

    if (!map.has(basePolicy)) {
      map.set(basePolicy, { taz: null, ownerSuffix: Infinity, memberId: policyStr.slice(6, 16), coverages: [] });
    }
    const entry = map.get(basePolicy);

    // Policy owner = first insured of the lowest-suffix sub-policy
    if (suffix < entry.ownerSuffix && insuredTaz) {
      entry.ownerSuffix = suffix;
      entry.taz = insuredTaz;
      entry.memberId = policyStr.slice(6, 16);
    }

    if (!entry.coverages.find(c => c.productCode === productCode && c.insuredTaz === insuredTaz)) {
      entry.coverages.push({ name: fundName, productCode, monthlyPremium, sumInsured, startDate, duration, insuredTaz });
    }
  }

  // Re-key by the owner's memberId (for RP/SB cross-referencing)
  const byMember = new Map();
  for (const entry of map.values()) {
    byMember.set(entry.memberId, { taz: entry.taz, coverages: entry.coverages });
  }
  return byMember;
}

// ────────────────────────────────────────────────────────────────────────────
// Extract the report MONTH from Harel file names.
// The name is a fixed code + 2-digit month in the LAST two digits — there is NO
// year in the filename (and no reliable value-date field inside the files), so the
// year is chosen by the user at import time.
//   RB1511805    → "05"   (code 15118 + month 05)
//   RM15118.05R  → "05"   (month in the .NNR extension)
// ────────────────────────────────────────────────────────────────────────────
function parseReportMonth(fileNames) {
  for (const name of fileNames) {
    const m = name.match(/\.(\d{2})R?$/i) || name.match(/(\d{2})$/);
    if (m && +m[1] >= 1 && +m[1] <= 12) return m[1]; // "MM"
  }
  return null;
}

// ────────────────────────────────────────────────────────────────────────────
// Main entry point.
// zipFiles: [{ name: string, content: ArrayBuffer }]
// Returns: { reportDate, members[] }
// ────────────────────────────────────────────────────────────────────────────
export function parseHarelFiles(zipFiles) {
  const rpEntry = zipFiles.find(f => /^RP\d+/i.test(f.name));
  const spEntry = zipFiles.find(f => /^SP\d+/i.test(f.name));
  const sbEntry = zipFiles.find(f => /^SB\d+/i.test(f.name));
  const rmEntry = zipFiles.find(f => /^RM\d+\.\d+R/i.test(f.name));
  const rbEntry = zipFiles.find(f => /^RB\d+/i.test(f.name));

  if (!rmEntry && !rbEntry && !rpEntry && !spEntry) {
    throw new Error('לא נמצאו קבצי הראל מתאימים (RM/RB/RP/SP)');
  }

  // Personal info: RP = pension/provident members, SP = insurance/savings members.
  // RP takes precedence when both have the same member ID.
  // tazNames maps EVERY taz (incl. family members on the same policy) → full name.
  const personalMap = new Map();
  const tazNames = new Map();
  if (spEntry) {
    for (const [k, v] of parsePersonalFile(spEntry.content, tazNames)) personalMap.set(k, v);
  }
  if (rpEntry) {
    for (const [k, v] of parsePersonalFile(rpEntry.content, tazNames)) personalMap.set(k, v);
  }

  const sbMap = sbEntry ? parseSBFile(sbEntry.content) : new Map();

  // ── RM members: pension / study-fund with investment-track breakdown ──────
  // Filter out RM members with no savings funds — they appear in the RM file
  // as zero-balance placeholders but their real products are health/life (RB/SB).
  const rmMembers = rmEntry
    ? parseRMFile(rmEntry.content, personalMap, sbMap).filter(m => m.funds.length > 0)
    : [];

  // ── RB members: health insurance products (מוצרי בריאות) ────────────────
  // A person can have BOTH savings (RM) and health (RB) products — always process both.
  const rbMap  = rbEntry ? parseRBFile(rbEntry.content) : new Map();
  const rbMembers = [];
  for (const [memberId, rbData] of rbMap) {
    if (!rbData.coverages?.length) continue;         // no coverages → skip

    const personal = personalMap.get(memberId) || {};
    const sb       = sbMap.get(memberId);
    const taz      = rbData.taz || personal.taz || memberId.slice(0, 9);

    // Health insurance: no savings balance, but carry premium + date info per coverage
    const funds = rbData.coverages.map(c => ({
      name: c.name,
      balance: 0,
      productCode: c.productCode,
      monthlyPremium: c.monthlyPremium || 0,
      sumInsured: c.sumInsured || 0,
      startDate: c.startDate || null,
      duration: c.duration || null,
      insuredTaz: c.insuredTaz || null,
      insuredName: (c.insuredTaz && tazNames.get(c.insuredTaz)) || null,
    }));

    rbMembers.push({
      rawId:       memberId,
      taz,
      firstName:   personal.firstName || null,
      lastName:    personal.lastName  || null,
      city:        personal.city      || null,
      gender:      personal.gender    || null,
      birthDate:   personal.birthDate || null,
      startDate:   sb?.startDate      || null,
      tochnit:     null,
      productType: 'health',
      funds,
    });
  }

  const members     = [...rmMembers, ...rbMembers];
  const reportMonth = parseReportMonth(zipFiles.map(f => f.name)); // "MM" or null
  return { reportMonth, members };
}

// Returns true if the given list of file entries looks like a Harel upload.
// Used for content-based detection when a ZIP is dropped.
export function isHarelContents(entries) {
  return entries.some(f => /^(RP|RB)\d+/i.test(f.name));
}
